import EventEmitter from 'eventemitter3';
import _ from 'lodash-es';
/**
 * Interface describing the session storage data structure
 */
export interface SessionData {
  /**
   * Timestamp of the last notification in milliseconds
   */
  lastNotifiedAt: number;

  /**
   * Tab ID of the current leader, or null if no leader
   */
  leader: string | null;

  /**
   * Timestamp of the leader's last ping in milliseconds
   */
  lastLeaderPingAt: number;

  /**
   * Timestamp when the current session started
   */
  sessionStartTime: number;
}

/**
 * Configuration options for SessionTimer
 */
export interface SessionTimerOptions {
  /**
   * Interval in milliseconds between notifications
   * @default 600000 (10 minutes)
   */
  notificationIntervalMs?: number;

  /**
   * Key to use for localStorage
   * @default 'session_data'
   */
  storageKey?: string;

  /**
   * Interval in milliseconds for leader to ping
   * @default 5000 (5 seconds)
   */
  leaderPingIntervalMs?: number;

  /**
   * Timeout in milliseconds after which a session is considered expired
   * If the last ping is older than this value, a new session will be created
   * @default undefined (no timeout)
   */
  sessionTimeoutMs?: number;
}

interface SessionTimerEvents {
  /**
   * Emitted when a new session is created
   */
  newSession: () => void;

  /**
   * Emitted when the leadership status changes
   */
  leadershipChange: (isLeader: boolean) => void;

  /**
   * Emitted when it's time to notify the leader
   */
  notification: () => void;
}

type OptionsKeyWithDefault = 'storageKey' | 'leaderPingIntervalMs' | 'notificationIntervalMs';
type ParsedSessionTimerOptions = Omit<SessionTimerOptions, OptionsKeyWithDefault> &
  Required<Pick<SessionTimerOptions, OptionsKeyWithDefault>>;

const defaultOptions: Required<Pick<SessionTimerOptions, OptionsKeyWithDefault>> = {
  storageKey: 'zl_session_timer',
  leaderPingIntervalMs: 5000,
  notificationIntervalMs: 600000,
};

const parseStorageData = (data: string | null) => {
  if (!data) {
    return null;
  }

  try {
    return JSON.parse(data) as SessionData;
  } catch (e) {
    console.error('Error parsing session data', e);
    return null;
  }
};

/**
 * SessionTimer manages session timing across multiple browser tabs
 * using localStorage for synchronization.
 */
export class SessionTimer extends EventEmitter<SessionTimerEvents> {
  private readonly tabId: string;

  private readonly options: ParsedSessionTimerOptions;

  private intervalId: number | null = null;

  private isPaused = false;

  private isInitialized = false;

  constructor(options: SessionTimerOptions) {
    super();

    // Generate a unique ID for this tab
    this.tabId = Date.now().toString() + Math.random().toString(36).substring(2, 11);

    // Set configuration options with defaults
    this.options = { ...defaultOptions, ...options };
  }

  /**
   * Initialize the session timer
   */
  public init(): void {
    if (this.isInitialized) return;

    const { leader } = this.getStorageData();

    // Try to become the leader
    this.tryBecomeLeader(leader);

    this.intervalId = window.setInterval(() => {
      this.checkAndNotify();
    }, this.options.leaderPingIntervalMs);

    // Handle storage events from other tabs
    window.addEventListener('storage', this.handleStorageEvent);

    // Handle page visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Handle page focus
    window.addEventListener('focus', this.handleVisibilityChange);

    // Handle page unload
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    this.isInitialized = true;

    // initial check
    this.checkAndNotify();
  }

  /**
   * Check if the current session has expired
   */
  private isSessionExpired(lastLeaderPingAt: number): boolean {
    if (!this.options.sessionTimeoutMs) return false;

    const now = Date.now();
    return now - lastLeaderPingAt > this.options.sessionTimeoutMs;
  }

  /**
   * Reset the session data, creating a new session
   */
  private resetSession(): void {
    const now = Date.now();
    this.updateStorageData({
      lastNotifiedAt: now,
      lastLeaderPingAt: now,
      sessionStartTime: now,
    });

    // Notify about new session if callback exists
    this.emit('newSession');
  }

  /**
   * Get session data from localStorage
   */
  public getStorageData(): SessionData {
    const data = localStorage.getItem(this.options.storageKey);
    const now = Date.now();

    return (
      parseStorageData(data) ?? {
        lastNotifiedAt: now,
        leader: null,
        lastLeaderPingAt: 0,
        sessionStartTime: now,
      }
    );
  }

  /**
   * Set session data in localStorage
   */
  private setStorageData(data: SessionData): void {
    localStorage.setItem(this.options.storageKey, JSON.stringify(data));
  }

  /**
   * Update fields in localStorage data
   */
  private updateStorageData(data: Partial<SessionData>): void {
    this.setStorageData({
      ...this.getStorageData(),
      ...data,
    });
  }

  /**
   * Try to become the leader tab that manages the timer
   */
  private tryBecomeLeader(currentLeader: string | null): void {
    // if the tab is hidden, do nothing
    if (document.visibilityState !== 'visible' || document.hidden) {
      return;
    }

    // If we're already the leader, do nothing
    if (currentLeader === this.tabId) {
      return;
    }

    this.updateStorageData({ leader: this.tabId });
    this.emit('leadershipChange', true);
  }

  /**
   * Check if it's time for a notification and send it
   */
  private checkAndNotify(): void {
    const { leader, lastNotifiedAt, lastLeaderPingAt } = this.getStorageData();

    const isLeader = this.isLeader(leader);

    // if the timer is paused, don't emit notifications, but keep the timer running
    if (isLeader && this.isPaused) {
      this.updateStorageData({ lastLeaderPingAt: Date.now() });
      return;
    }

    // Check if session has expired
    if (isLeader && this.isSessionExpired(lastLeaderPingAt)) {
      this.resetSession();
      return;
    }

    const now = Date.now();
    const timeSinceLastNotification = now - lastNotifiedAt;

    // If it's been at least the specified interval since the last notification
    if (timeSinceLastNotification >= this.options.notificationIntervalMs) {
      this.triggerNotificationNow();
    }

    // if the current tab is the leader, update the last ping time
    if (isLeader) {
      this.updateStorageData({ lastLeaderPingAt: Date.now() });
    }
  }

  /**
   * Handle storage events from other tabs
   */
  private handleStorageEvent = (event: StorageEvent): void => {
    if (event.key !== this.options.storageKey) return;

    const newData = parseStorageData(event.newValue);
    if (!newData) return;

    const oldData = parseStorageData(event.oldValue);

    const wasLeader = oldData?.leader === this.tabId;
    const isLeader = newData.leader === this.tabId;

    // Since the event is not triggered when the change is from the current tab,
    // we trigger the event when we become leader instead of here

    if (!isLeader && wasLeader) {
      // if we are demoted from leader, notify about the change
      this.emit('leadershipChange', false);
    }

    // if there is no leader, try to become one
    if (newData.leader == null) {
      this.tryBecomeLeader(newData.leader);
    }
  };

  /**
   * Handle visibility changes
   */
  private handleVisibilityChange = _.debounce(() => {
    if (document.visibilityState === 'visible') {
      // refresh the data to get the latest data
      const data = this.getStorageData();

      // Try to become leader when the tab becomes visible
      this.tryBecomeLeader(data.leader);
    }
  }, 10);

  /**
   * Handle tab/window close
   */
  private handleBeforeUnload = () => {
    // If this tab is the leader, remove leadership
    const data = this.getStorageData();
    if (data.leader === this.tabId) {
      data.leader = null;
      this.setStorageData(data);
    }
  };

  /**
   * Check if this tab is currently the leader
   * @returns true if this tab is the leader, false otherwise
   */
  public isLeader(leader: string | null) {
    return leader === this.tabId;
  }

  /**
   * Trigger a notification after a specified number of milliseconds
   * @param ms The number of milliseconds to wait before triggering the notification
   */
  public triggerNotificationAfter(ms: number): void {
    const { leader } = this.getStorageData();
    if (!this.isLeader(leader)) return;
    this.updateStorageData({ lastNotifiedAt: Date.now() + this.options.notificationIntervalMs - ms });
  }

  /**
   * Force a session notification immediately
   */
  public triggerNotificationNow(): void {
    const { leader } = this.getStorageData();
    if (this.isLeader(leader)) {
      this.updateStorageData({ lastNotifiedAt: Date.now() });
    }
    // prevent queue notifications when the tab is hidden
    if (document.visibilityState === 'visible') {
      this.emit('notification');
    }
  }

  /**
   * Clean up event listeners and intervals
   */
  public destroy(): void {
    window.removeEventListener('storage', this.handleStorageEvent);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.removeAllListeners();

    if (this.intervalId) {
      window.clearInterval(this.intervalId);
    }

    const { leader } = this.getStorageData();
    if (this.isLeader(leader)) {
      this.updateStorageData({ leader: null });
      this.emit('leadershipChange', false);
    }

    this.isInitialized = false;
  }

  public pause(): void {
    this.isPaused = true;
  }

  public resume(): void {
    this.isPaused = false;
  }

  public resetCountdown(): void {
    const { leader } = this.getStorageData();
    if (!this.isLeader(leader)) return;
    this.updateStorageData({ lastNotifiedAt: Date.now() });
  }

  public clearData(): void {
    localStorage.removeItem(this.options.storageKey);
  }
}

/**
 * Creates a new session timer that notifies at specified intervals
 * @param options Configuration options
 * @returns SessionTimer instance
 */
export function createSessionTimer(options: SessionTimerOptions): SessionTimer {
  return new SessionTimer(options);
}
